/**
 * The copilot's answer, rendered as the small amount of Markdown it actually emits.
 *
 * An ordinary answer is a paragraph or two and needs none of this. An ANALYSIS does:
 * the engine writes a bold heading and a numbered list of causes, in one string,
 * because it has one output for every reader it has. Rendered as plain text that
 * prints its own asterisks and list markers on the most important answer the product
 * gives — and it is the answer a maintenance engineer is deciding whether to trust.
 *
 * ── Why this and not a Markdown library ──────────────────────────────────
 * The input is model output. A general renderer brings a much larger surface than
 * the three constructs that arrive — raw HTML passthrough, links, images, tables —
 * and every one of those is something the engine never sends and an answer could
 * nonetheless contain. This handles bold, ordered lists and unordered lists, and
 * anything else reaches the screen as the literal characters the model wrote, which
 * is the honest failure: visible, inert, and not a claim about anything.
 *
 * Nothing here parses HTML and nothing is set as HTML. `dangerouslySetInnerHTML`
 * must never appear in this file.
 */

/** `**bold**` → nodes. Split rather than replaced, so the text itself is never re-parsed. */
function inline(text, keyPrefix) {
  return text.split(/\*\*([^*]+)\*\*/g).map((part, index) =>
    /* The capture groups land on the odd indices — that is what `split` with a
       capturing pattern does, and it is why this needs no second regex. */
    index % 2 === 1 ? (
      <strong key={`${keyPrefix}-${index}`} className="font-medium text-ink">
        {part}
      </strong>
    ) : (
      part
    ),
  )
}

const ORDERED = /^\s*\d+[.)]\s+/
/* `-` and `•` only. `*` is deliberately not a bullet here: the engine does not use
   it as one, and treating it as one would make the first word of `**Most likely
   causes**` into a list item. */
const UNORDERED = /^\s*[-•]\s+/

/** Lines → paragraphs and lists, in one pass. A run of list lines is one list. */
function blocksOf(text) {
  const blocks = []
  let paragraph = []

  const flush = () => {
    if (paragraph.length) blocks.push({ kind: 'p', lines: paragraph })
    paragraph = []
  }

  for (const line of text.split('\n')) {
    const ordered = ORDERED.test(line)
    const unordered = !ordered && UNORDERED.test(line)

    if (!ordered && !unordered) {
      if (line.trim() === '') flush()
      else paragraph.push(line)
      continue
    }

    flush()
    const kind = ordered ? 'ol' : 'ul'
    const item = line.replace(ordered ? ORDERED : UNORDERED, '')
    const last = blocks[blocks.length - 1]
    if (last?.kind === kind) last.items.push(item)
    else blocks.push({ kind, items: [item] })
  }

  flush()
  return blocks
}

export function Prose({ text = '' }) {
  if (!text) return null

  return blocksOf(text).map((block, index) => {
    if (block.kind === 'p') {
      return (
        /* `whitespace-pre-wrap` because a single newline inside a paragraph is a
           line break the writer meant — this is not a document format where they
           collapse. */
        <p key={index} className="whitespace-pre-wrap mt-2.5 first:mt-0">
          {inline(block.lines.join('\n'), index)}
        </p>
      )
    }

    const List = block.kind === 'ol' ? 'ol' : 'ul'
    return (
      <List
        key={index}
        className={
          block.kind === 'ol'
            ? 'ms-4 list-decimal mt-2.5 first:mt-0 marker:text-ink-mute'
            : 'ms-4 list-disc mt-2.5 first:mt-0 marker:text-ink-mute'
        }
      >
        {block.items.map((item, position) => (
          <li key={position} className="ps-0.5">
            {inline(item, `${index}-${position}`)}
          </li>
        ))}
      </List>
    )
  })
}
